import { useEffect, useState } from "react";
import { IResource, IRole } from "./../../../commons/interfaces";

import Helpers from "../../../commons/helpers";
import { Constants } from "../../../constants/constants";
import { ResourceType } from "../../../constants/enums";
import Strings from "../../../constants/strings";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import ResourceService, { IRequestGetPagedResource } from "../../../services/identity/resourceService";
import RoleService from "../../../services/identity/role.serice";

export interface IListRoleModel {
    requestData?: IRequestGetPagedResource;
    requestDataTemp?: IRequestGetPagedResource;
}

interface IProps {
    request: IRequestGetPagedResource;
    onGetPaged: (data: { query: string; totalCount: number }) => void;
}

export const useData = ({ request, onGetPaged }: IProps) => {
    const {
        clientId,
        userInfo,
        tenantCode,
        serviceCode,
        onError = () => { },
        onSuccess = () => { },
        onShowLoading = () => { },
        onHideLoading = () => { },
    } = useCommonComponentContext();

    const [model, setModel] = useState<IListRoleModel>({} as IListRoleModel);
    const [loadingDataTable, setLoadingDataTable] = useState<boolean>(false);
    const [rows, setRows] = useState<IResource[]>([]);

    useEffect(() => {
        const onStarted = async () => {
            const newReq: IRequestGetPagedResource = {
                totalCount: request?.totalCount || 0,
                pageSize: request?.pageSize || model?.requestData?.pageSize,
                pageNumber: request?.pageNumber || model?.requestData?.pageNumber,
                searchText: request?.searchText || model?.requestData?.searchText,
                serviceCode: request?.serviceCode || model?.requestData?.serviceCode,
                resourceType: request?.resourceType || model?.requestData?.resourceType,
            };

            await getPaged(newReq);
        };
        onStarted();
    }, [request, userInfo?.userProfile?.organizationId]);

    const getPaged = async (newReq?: IRequestGetPagedResource) => {
        try {
            // onShowLoading();
            setLoadingDataTable(true);

            const pageSize = newReq?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(newReq?.pageNumber || 1, pageSize, newReq?.totalCount || 0) || 1;

            const requestData: IRequestGetPagedResource = {
                pageSize,
                pageNumber,
                clientId,
                searchText: newReq?.searchText,
                serviceCode: newReq?.serviceCode,
                resourceType: ResourceType.Module,
                parentResource: newReq?.parentResource,
                organizationId: userInfo?.userProfile?.organizationId,
            };

            const resultGetPaged = await ResourceService.getPaged(requestData);

            let query: string = `?pageNumber=${requestData.pageNumber}&pageSize=${requestData.pageSize}`;
            if (!Helpers.isNullOrEmpty(requestData?.serviceCode)) {
                query += `&serviceCode=${requestData?.serviceCode}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.parentResource)) {
                query += `&parentResource=${requestData?.parentResource}`;
            }
            if (!Helpers.isNullOrEmpty(requestData?.searchText)) {
                query += `&searchText=${requestData?.searchText}`;
            }

            setRows(resultGetPaged.items);
            setModel({
                requestData: {
                    ...requestData,
                    totalCount: resultGetPaged.totalCount,
                },
                requestDataTemp: {
                    ...requestData,
                    totalCount: resultGetPaged.totalCount,
                },
            });

            onGetPaged({ query, totalCount: resultGetPaged.totalCount });
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            // onHideLoading();
            setLoadingDataTable(false);
        }
    };

    const handleDelete = (row: any) => {
        Helpers.showConfirmAlert(Strings.MESSAGE.CONFIRM_DELETE, async () => {
            try {
                onShowLoading();

                await ResourceService.delete([row?.id]);

                await getPaged(model.requestData);

                onSuccess(Strings.MESSAGE.DELETE_SUCCESS);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        });
    };

    const handleDeleteMultiple = (data: string[]) => {
        Helpers.showConfirmAlert(Strings.MESSAGE.CONFIRM_DELETE, async () => {
            try {
                onShowLoading();

                await ResourceService.delete(data);

                await getPaged(model.requestData);

                onSuccess(Strings.MESSAGE.DELETE_SUCCESS);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        });
    };

    const onChangeValue = (key: keyof IRequestGetPagedResource) => (value: string | number) => {
        setModel((prev) => ({ ...prev, requestDataTemp: { ...prev.requestDataTemp, [key]: value } }));
    };

    const onResetModel = () => setModel({ ...model, requestDataTemp: model.requestData });

    return {
        rows,
        model,
        getPaged,
        onResetModel,
        handleDelete,
        onChangeValue,
        handleDeleteMultiple,
        loadingDataTable,
    };
};

export default useData;
